import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";

interface LocalSEOHubFAQProps {
  language: 'es' | 'en';
}

export const LocalSEOHubFAQ = ({ language }: LocalSEOHubFAQProps) => {
  const content = {
    es: {
      title: "Preguntas frecuentes sobre SEO Local",
      faqs: [
        {
          question: "¿El SEO local funciona para empresas B2B?",
          answer: "Sí. Muchas búsquedas B2B tienen intención local: 'proveedor industrial Houston', 'consultor legal cerca de mí'. El SEO local posiciona tu empresa cuando potenciales clientes buscan proveedores en su zona."
        },
        {
          question: "¿Necesito una oficina física para hacer SEO local?",
          answer: "No necesariamente. Si atiendes clientes en ubicaciones específicas (visitas a clientes, entregas, servicios en sitio), puedes optimizar para esas áreas como 'área de servicio' sin tener oficina física allí."
        },
        {
          question: "¿Cuánto tiempo toma ver resultados en SEO local?",
          answer: "El SEO local puede mostrar mejoras observables entre 3 y 6 meses, dependiendo de la competencia local, el estado actual de tu perfil, y la consistencia de la optimización. Los mercados menos competitivos pueden ver resultados más rápido."
        },
        {
          question: "¿Puedo hacer SEO local en varias ciudades?",
          answer: "Sí. Si atiendes múltiples ubicaciones, podemos crear estrategias de SEO local para cada área con páginas de servicio geolocalizado, citaciones específicas, y contenido relevante para cada mercado."
        },
        {
          question: "¿Qué diferencia hay entre SEO local y SEO tradicional?",
          answer: "El SEO local se enfoca específicamente en búsquedas con intención geográfica y optimiza elementos como Google Business Profile, reseñas, citaciones NAP, y el Local Pack. El SEO tradicional trabaja posicionamiento orgánico general sin enfoque geográfico."
        }
      ]
    },
    en: {
      title: "Frequently Asked Questions About Local SEO",
      faqs: [
        {
          question: "Does local SEO work for B2B companies?",
          answer: "Yes. Many B2B searches have local intent: 'industrial supplier Houston', 'legal consultant near me'. Local SEO positions your company when potential clients search for providers in their area."
        },
        {
          question: "Do I need a physical office for local SEO?",
          answer: "Not necessarily. If you serve clients in specific locations (client visits, deliveries, on-site services), you can optimize for those areas as 'service area' without having a physical office there."
        },
        {
          question: "How long does it take to see results in local SEO?",
          answer: "Local SEO can show observable improvements between 3 and 6 months, depending on local competition, current profile status, and optimization consistency. Less competitive markets may see results faster."
        },
        {
          question: "Can I do local SEO in multiple cities?",
          answer: "Yes. If you serve multiple locations, we can create local SEO strategies for each area with geolocated service pages, specific citations, and relevant content for each market."
        },
        {
          question: "What's the difference between local SEO and traditional SEO?",
          answer: "Local SEO specifically focuses on searches with geographic intent and optimizes elements like Google Business Profile, reviews, NAP citations, and the Local Pack. Traditional SEO works on general organic positioning without geographic focus."
        }
      ]
    }
  };

  const t = content[language];

  // Generate FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": t.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section className="section-padding bg-background">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand/10 rounded-full text-brand text-sm font-medium mb-4">
              <HelpCircle className="w-4 h-4" />
              FAQ
            </div>
            <h2 className="text-3xl md:text-4xl font-bold">
              {t.title}
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="w-full">
            {t.faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left font-semibold">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
      
      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </section>
  );
};
